import {
  ColDef,
  ColGroupDef,
  ICellRendererParams,
  ValueGetterParams
} from "ag-grid-enterprise";
import {
  ActionsUnTrackedSubmittalCellRenderer,
  CellValueRendererWithEditIcon,
  specSectionLabel
} from "components/cell-renders";
import { SubmittalStatusToStrMap } from "constants/index";
import { ProjectContext } from "context/ProjectProvider";
import {
  ArrayStringGetQuickFilterText,
  submittalStatusCellRendererV2,
  twoRowCellRenderer,
  twoRowGetQuickFilterText
} from "entity-app/shared-components/log-render-components/cell-renders";
import {
  coldefHiddenField,
  coldefNavigation,
  coldefSingleField,
  coldefUser
} from "entity-app/shared-components/log-render-components/col-defs";
import { submittalSequenceComparator } from "pages/submittals-log-page/utils";
import { useContext, useMemo } from "react";
import { comparatorWithEmptyAtBottom } from "utils/grid-utils";

function useSubmittalTrackedGridConfig() {
  const {
    columnHeaders: { submittalHeaderMap },
    projectDetails
  } = useContext(ProjectContext);

  const gridDefaultColDef = useMemo(() => {
    return {
      sortable: true,
      editable: false,
      filter: true,
      resizable: true,
      cellEditorPopup: false,
      comparator: comparatorWithEmptyAtBottom,
      cellRenderer: (params: ICellRendererParams) => {
        return CellValueRendererWithEditIcon(params, false);
      }
    } as ColDef<any>;
  }, []);
  const gridColumnDefs = useMemo(() => {
    return [
      coldefNavigation(
        {
          field: "sequence_id",
          colId: "submittal_sequence_id",
          headerName: submittalHeaderMap?.submittal_id,
          pinned: "left",
          lockPosition: "left",
          initialWidth: 80,
          filter: false,
          sortable: true,
          editable: false,
          valueGetter: (params: ValueGetterParams) => {
            const { context, data } = params;
            if (!data.sequence_id) return "";
            const value = data.sequence_id || "";
            const cellText =
              context.projectDetails?.spec_section &&
              data.spec_section.length > 0
                ? `${data.spec_section[0].number} - ${data?.sequence_id}`
                : `${value}`;
            return cellText;
          },
          comparator: (valueA: any, valueB: any, nodeA: any, nodeB: any) =>
            submittalSequenceComparator(
              nodeA.data.sequence_id,
              nodeB.data.sequence_id,
              nodeA,
              nodeB,
              projectDetails?.spec_section
            )
        },
        projectDetails?.id as string,
        "submittals"
      ),
      coldefSingleField({
        field: "number",
        colId: "submittal_number",
        headerName: submittalHeaderMap?.submittal_number,
        pinned: "left",
        lockPosition: "left",
        initialWidth: 120
      }),
      coldefSingleField({
        field: "title",
        colId: "title",
        headerName: submittalHeaderMap?.title,
        filter: true,
        sortable: true,
        initialWidth: 180,
        pinned: "left",
        lockPosition: "left",
        getQuickFilterText: twoRowGetQuickFilterText,
        cellRenderer: twoRowCellRenderer,
        cellRendererParams: {
          featureKey: "submittals",
          projectId: projectDetails?.id as string
        },
        lockVisible: true,
        comparator(valueA: any, valueB: any, nodeA: any, nodeB: any) {
          const compareA = nodeA?.data?.title ?? nodeA?.data?.description;
          const compareB = nodeB?.data?.title ?? nodeB?.data?.description;
          return compareA?.toLowerCase().localeCompare(compareB?.toLowerCase());
        }
      }),
      coldefHiddenField({
        field: "description",
        headerName: "Description",
        pinned: "left",
        lockPosition: "left"
      }),
      coldefSingleField({
        field: "workflow_status",
        colId: "status",
        headerName: submittalHeaderMap?.workflow_status,
        lockVisible: true,
        initialWidth: 200,
        minWidth: 200,
        tooltipField: undefined,
        tooltipValueGetter: undefined,
        cellRenderer: submittalStatusCellRendererV2,
        valueGetter: (params: ValueGetterParams) => {
          return SubmittalStatusToStrMap[params.data.workflow_status.id];
        }
      }),
      coldefSingleField({
        field: "type.value",
        colId: "type",
        headerName: submittalHeaderMap?.type || "",
        headerTooltip: submittalHeaderMap?.type || "",
        tooltipField: undefined,
        initialWidth: 150,
        minWidth: 100,
        lockVisible: true
      }),
      coldefSingleField({
        field: "spec_section",
        colId: "custom_spec_section",
        headerName: submittalHeaderMap?.spec_no,
        headerTooltip: submittalHeaderMap?.spec_no,
        getQuickFilterText: ArrayStringGetQuickFilterText,
        valueGetter: ({ data }) => {
          if (!data?.spec_section?.length) return "";
          const spec = data.spec_section[0];
          const specLabel = specSectionLabel({
            specNumber: spec?.number,
            specName: spec?.name
          });

          return specLabel;
        },
        tooltipField: undefined,
        initialWidth: 300,
        minWidth: 300
      }),
      coldefUser({
        def: {
          field: "responsible_contractor",
          colId: "responsible_contractor",
          headerName: submittalHeaderMap?.responsible_contractor,
          initialWidth: 220
        },
        user: {
          id: "responsible_contractor.id",
          firstName: "responsible_contractor.name",
          lastName: "",
          dropdownType: "RC_dropdown",
          participantsSource: "responsibleContractors"
        }
      }),
      coldefUser({
        def: {
          field: "submitter_reviewer",
          colId: "submitter",
          headerName: submittalHeaderMap?.submitter,
          initialWidth: 180
        },
        user: {
          id: "submitter_reviewer.id",
          firstName: "submitter_reviewer.first_name",
          lastName: "submitter_reviewer.last_name",
          statusId: "submitter_reviewer.status_id",
          companyObjField: "submitter_reviewer.company",
          dependentId: "responsible_contractor.id",
          dependentNotFoundContent: "First select a Responsible Contractor",
          dropdownType: "RC_ASSIGNEE_dropdown"
        }
      }),
      coldefUser({
        def: {
          field: "gc_user",
          colId: "gc_reviewer",
          headerName: submittalHeaderMap?.gc_reviewer,
          initialWidth: 180
        },
        user: {
          id: "gc_user.id",
          firstName: "gc_user.first_name",
          lastName: "gc_user.last_name",
          statusId: "gc_user.status_id",
          companyObjField: "gc_user.company"
        }
      }),
      {
        colId: "action",
        field: "submittal_schedule_links",
        headerName: "Actions",
        headerTooltip: "Actions",
        pinned: "right",
        initialWidth: 100,
        filter: false,
        sortable: false,
        menuTabs: [],
        cellRenderer: ActionsUnTrackedSubmittalCellRenderer,
        suppressColumnsToolPanel: true,
        getQuickFilterText: () => ""
      }
    ] as ColDef<any, any>[] | ColGroupDef<any>[];
  }, [projectDetails, submittalHeaderMap]);

  return {
    gridDefaultColDef,
    gridColumnDefs
  };
}

export default useSubmittalTrackedGridConfig;
